import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext(null);

const SYMBOLS = {
    GBP: "£", USD: "$", EUR: "€", JPY: "¥", AUD: "A$",
    CAD: "C$", CHF: "CHF ", HKD: "HK$", SGD: "S$", AED: "AED ",
};

// Best-guess default currency from the browser locale
function guessCurrency(supported) {
    try {
        const region = (navigator.language || "en-GB").split("-")[1]?.toUpperCase();
        const byRegion = {
            US: "USD", CA: "CAD", AU: "AUD", NZ: "AUD",
            JP: "JPY", HK: "HKD", SG: "SGD", CH: "CHF",
            AE: "AED",
            DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", IE: "EUR",
            PT: "EUR", AT: "EUR", BE: "EUR", FI: "EUR", GR: "EUR", LU: "EUR",
        };
        const guess = byRegion[region];
        if (guess && supported.includes(guess)) return guess;
    } catch { /* noop */ }
    return "GBP";
}

export function CurrencyProvider({ children }) {
    const [rates, setRates] = useState({ GBP: 1 });
    const [supported, setSupported] = useState(["GBP"]);
    const [currency, setCurrencyState] = useState("GBP");
    const [ready, setReady] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/currency/rates");
                setRates(data.rates || { GBP: 1 });
                setSupported(data.supported || Object.keys(data.rates || {}));
                const saved = localStorage.getItem("rax_currency");
                if (saved && (data.supported || []).includes(saved)) {
                    setCurrencyState(saved);
                } else {
                    setCurrencyState(guessCurrency(data.supported || []));
                }
            } catch {
                // stay on GBP-only
            } finally {
                setReady(true);
            }
        })();
    }, []);

    const setCurrency = useCallback((code) => {
        setCurrencyState(code);
        try { localStorage.setItem("rax_currency", code); } catch { /* noop */ }
    }, []);

    // format GBP amount into current currency; returns { primary, reference }
    const format = useCallback((gbpAmount) => {
        const n = Number(gbpAmount) || 0;
        const primary = `£${n.toLocaleString("en-GB", { minimumFractionDigits: 0 })}`;
        if (!currency || currency === "GBP" || !rates[currency]) {
            return { primary, reference: null, symbol: "£", value: n, currency: "GBP" };
        }
        const rate = rates[currency];
        const converted = n * rate;
        const symbol = SYMBOLS[currency] || `${currency} `;
        const digits = currency === "JPY" ? 0 : 0; // keep clean
        const ref = `${symbol}${converted.toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
        return { primary, reference: `≈ ${ref} ${currency}`, symbol, value: converted, currency };
    }, [currency, rates]);

    const value = useMemo(() => ({ currency, setCurrency, rates, supported, ready, format, SYMBOLS }),
        [currency, setCurrency, rates, supported, ready, format]);

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    return useContext(CurrencyContext) || {
        currency: "GBP", setCurrency: () => {}, rates: { GBP: 1 }, supported: ["GBP"],
        ready: false, SYMBOLS,
        format: (n) => ({ primary: `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`, reference: null }),
    };
}
